import type {
  ExtractionStatus,
  InboundDocumentKind,
  InboundStatus,
  MissingItemsResolution,
  ReceiptOutcome,
} from "@nexxt/contracts/procurement";
import { hasOutcomeToConfirm } from "@nexxt/contracts/procurement/rules";
import { m } from "../paraglide/messages.js";

// An inbound in the screens' words (pure, with its tests): its states, the kinds of its documents, who it waits on,
// and which documents the browser can show next to the packing list (ADR-132).

export const inboundStatusLabel: Record<InboundStatus, () => string> = {
  draft: m.inbound_status_draft,
  documents_uploaded: m.inbound_status_documents_uploaded,
  pending_authorization: m.inbound_status_pending_authorization,
  authorized: m.inbound_status_authorized,
  lines_captured: m.inbound_status_lines_captured,
  in_exception: m.inbound_status_in_exception,
  three_way_passed: m.inbound_status_three_way_passed,
  ddt_generated: m.inbound_status_ddt_generated,
  in_transit: m.inbound_status_in_transit,
  partially_received: m.inbound_status_partially_received,
  received: m.inbound_status_received,
  closed: m.inbound_status_closed,
  cancelled: m.inbound_status_cancelled,
};

/** The reading of the packing list by the extraction tool (ADR-233): only Tomorrow reads it. */
export const extractionStatusLabel: Record<ExtractionStatus, () => string> = {
  requested: m.extraction_status_requested,
  captured: m.extraction_status_captured,
  to_review: m.extraction_status_to_review,
  reviewed: m.extraction_status_reviewed,
  failed: m.extraction_status_failed,
};

export const inboundDocumentKindLabel: Record<InboundDocumentKind, () => string> = {
  packing_list: m.document_type_packing_list,
  supplier_invoice: m.inbound_document_supplier_invoice,
  waybill: m.inbound_document_waybill,
  credit_note_brand: m.inbound_document_credit_note_brand,
  credit_note_3pl: m.inbound_document_credit_note_3pl,
  photo: m.inbound_document_photo,
  other: m.inbound_document_other,
};

/** The step an inbound waits for, and so who does it (flussi-per-ruolo §3, F-04). */
type Waiting =
  | "supplier"
  | "brand"
  | "capture"
  | "check"
  | "exception"
  | "dispatch"
  | "warehouse"
  | "goods"
  | "rest"
  | "outcome"
  | "missing";

const waitingFor: Partial<Record<InboundStatus, Waiting>> = {
  draft: "supplier",
  pending_authorization: "brand",
  documents_uploaded: "capture",
  authorized: "capture",
  lines_captured: "check",
  in_exception: "exception",
  three_way_passed: "dispatch",
  ddt_generated: "warehouse",
  in_transit: "goods",
  // ADR-138: the warehouse received a part, and expects the rest
  partially_received: "rest",
};

type WaitingText = (p: { since: string; supplier: string; brand: string }) => string;

const waitingTexts: Record<Waiting, WaitingText> = {
  supplier: m.inbound_waiting_on_supplier,
  brand: m.inbound_waiting_on_brand,
  capture: m.inbound_waiting_on_capture,
  check: m.inbound_waiting_on_check,
  exception: m.inbound_waiting_on_exception,
  dispatch: m.inbound_waiting_on_dispatch,
  warehouse: m.inbound_waiting_on_warehouse,
  goods: m.inbound_waiting_on_goods,
  rest: m.inbound_waiting_on_rest,
  outcome: m.inbound_waiting_on_outcome,
  missing: m.inbound_waiting_on_missing,
};

/**
 * The steps of Tomorrow in the words of the supplier and of the brand, who do not know its teams (ADR-169 point 6:
 * the words depend on who reads, never the moves).
 */
const externalWaitingTexts: Partial<Record<Waiting, WaitingText>> = {
  capture: m.inbound_waiting_on_tomorrow_capture,
  check: m.inbound_waiting_on_tomorrow_check,
  exception: m.inbound_waiting_on_tomorrow_exception,
  dispatch: m.inbound_waiting_on_tomorrow_dispatch,
  outcome: m.inbound_waiting_on_tomorrow_outcome,
};

/** The supplier's choice for the units missing (ADR-140). */
export const missingItemsResolutionLabel: Record<MissingItemsResolution, () => string> = {
  credit_note: m.missing_items_credit_note,
  ship_later: m.missing_items_ship_later,
  credit_note_and_ship_later: m.missing_items_both,
};

export const receiptOutcomeLabel: Record<ReceiptOutcome, () => string> = {
  no_discrepancy: m.inbound_outcome_no_discrepancy,
  excess: m.inbound_outcome_excess,
  shortage: m.inbound_outcome_shortage,
  excess_and_shortage: m.inbound_outcome_excess_and_shortage,
};

/** An inbound as who it waits on reads it. */
interface WaitingInbound {
  status: InboundStatus;
  supplier: string;
  brand: string;
  receiptOutcome: ReceiptOutcome | null;
  outcomeConfirmedAt: string | null;
}

/**
 * Received, an inbound waits for Logistics to confirm an outcome with a difference, then, with units missing, for the
 * supplier's choice (ADR-139).
 */
function waitingOf(inbound: WaitingInbound): Waiting | undefined {
  if (inbound.status !== "received") return waitingFor[inbound.status];
  if (hasOutcomeToConfirm(inbound)) return "outcome";
  return inbound.outcomeConfirmedAt !== null ? "missing" : undefined;
}

/**
 * Who the inbound waits on (design-system.md §4.6), from its state; nothing once it is done. An external user reads
 * Tomorrow where an internal one reads the team.
 */
export function inboundWaitingOn(inbound: WaitingInbound, since: string, isInternal: boolean): string | null {
  const waiting = waitingOf(inbound);
  if (!waiting) return null;
  const text = (!isInternal && externalWaitingTexts[waiting]) || waitingTexts[waiting];
  return text({ since, supplier: inbound.supplier, brand: inbound.brand });
}

/**
 * The types the files worker accepts in the inbound folder and the browser shows by itself (ADR-069, ADR-132): a PDF
 * in its viewer, a photo as an image. The type is the one the server decided from the content of the file.
 */
const previewTypes = ["application/pdf", "image/png", "image/jpeg"] as const;
export type PreviewType = (typeof previewTypes)[number];

export const previewTypeOf = (mimeType: string | null): PreviewType | undefined =>
  previewTypes.find((type) => type === mimeType);

/** The documents to type the packing list from: the ones the browser can show, the packing lists first. */
export function previewDocuments<D extends { kind: InboundDocumentKind; mimeType: string | null }>(
  documents: D[],
): D[] {
  const shown = documents.filter((doc) => previewTypeOf(doc.mimeType) !== undefined);
  return [...shown.filter((doc) => doc.kind === "packing_list"), ...shown.filter((doc) => doc.kind !== "packing_list")];
}
